import { useState, type FormEvent } from "react";
import { PALETTE, type DisplayColor } from "@mmm/contracts";
import { Modal } from "../shell/Modal.js";
import { DialogActions } from "../molecules/DialogActions.js";
import { ColorSwatchPicker } from "../ui/ColorSwatchPicker.js";
import { Button, Input, useT } from "@mmm/ui";
interface SubjectDialogProps {
  initialTitle?: string;
  initialColor?: DisplayColor;
  mode: "create" | "edit";
  onSubmit: (values: { title: string; color: DisplayColor }) => Promise<void>;
  onClose: () => void;
  /** Edit-mode management (subjects are managed from their parent screens' cards, via this dialog). */
  archived?: boolean;
  onArchiveToggle?: () => void;
  onDelete?: () => void;
}

export function SubjectDialog({
  initialTitle = "",
  initialColor = PALETTE[0],
  mode,
  onSubmit,
  onClose,
  archived = false,
  onArchiveToggle,
  onDelete,
}: SubjectDialogProps) {
  const t = useT();
  const [title, setTitle] = useState(initialTitle);
  const [color, setColor] = useState<DisplayColor>(initialColor);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ title: trimmedTitle, color });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("subjects.saveFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2>{mode === "edit" ? t("subjects.editSubject") : t("subjects.newSubject")}</h2>
      <form onSubmit={handleSubmit}>
        <label className="mmm-field-label" htmlFor="subject-name">
          {t("common.name")}
        </label>
        <Input
          id="subject-name"
          autoFocus
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t("subjects.namePlaceholder")}
        />

        <div className="mmm-field-block">
          <label className="mmm-field-label">{t("common.color")}</label>
          <ColorSwatchPicker value={color} onChange={setColor} />
        </div>

        {error && <p role="alert" className="mmm-form-error mmm-form-error--spaced">{error}</p>}

        {mode === "edit" && (
          <div className="mmm-dialog-management-actions">
            <Button variant="secondary" onClick={onArchiveToggle}>
              {archived ? t("common.unarchive") : t("common.archive")}
            </Button>
            <Button variant="danger" onClick={onDelete}>
              {t("subjects.deleteSubject")}
            </Button>
          </div>
        )}

        <DialogActions
          onCancel={onClose}
          submitting={submitting}
          confirmDisabled={!title.trim()}
          confirmLabel={
            submitting
              ? mode === "edit"
                ? t("common.saving")
                : t("common.creating")
              : mode === "edit"
                ? t("common.save")
                : t("common.create")
          }
        />
      </form>
    </Modal>
  );
}
